import React from 'react'
import Head from 'next/head'
import { AboutUsWrapper } from '../components/about-us/styles'
import Hero from '../components/about-us/Hero'
import MissionStats from '../components/about-us/MissionStats'
import Founders from '../components/about-us/Founders'
import FoundersQuote from '../components/about-us/FoundersQuote'
import TopTierTalent from '../components/about-us/TopTierTalent'
import Partnerships from '../components/about-us/Partnerships'
import TestimonialsCarousel from '../components/about-us/TestimonialsCarousel'
import PressLogos from '../components/about-us/PressLogos'
import BookCTA from '../components/about-us/BookCTA'
import MarketsMap from '../components/about-us/MarketsMap'
import FAQsCTA from '../components/about-us/FAQsCTA'

const TITLE = 'About GLAMSQUAD - Our Story, Founders, and Why We Bring Beauty to You'
const DESCRIPTION = 'Meet the founders behind GLAMSQUAD, the on-demand beauty platform bringing licensed hair, makeup, and nail pros to your home. Discover our story.'
const CANONICAL = 'https://glamsquad.com/about-us'
const SHARE_IMAGE = 'https://glamsquad.com/about-us/hero.jpg'

// AboutPage entity-hub markup. Nests the Organization under the canonical
// #organization @id (defined in src/schemas/organization.json and served on the
// homepage) so search engines merge the two into a single brand entity. Surfaces
// the founders (with LinkedIn sameAs), the homepage AggregateRating, and the
// three about-page stat blocks as InteractionCounters (kept in sync with
// src/components/about-us/MissionStats.js).
const ABOUT_PAGE_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  '@id': 'https://glamsquad.com/about-us#aboutpage',
  url: 'https://glamsquad.com/about-us',
  name: 'About Us | GLAMSQUAD',
  description: DESCRIPTION,
  mainEntity: {
    '@type': 'Organization',
    '@id': 'https://glamsquad.com/#organization',
    name: 'GLAMSQUAD',
    legalName: 'Glamsquad Inc.',
    url: 'https://glamsquad.com/',
    logo: 'https://glamsquad.com/images/new-gs-wordmark-logo.svg',
    description:
      'GLAMSQUAD is an on-demand beauty services platform that brings licensed, background-checked hair stylists, makeup artists, and nail professionals directly to your home, hotel, or office.',
    foundingDate: '2013',
    sameAs: [
      'https://www.instagram.com/glamsquad/',
      'https://www.facebook.com/GlamSquad/',
      'https://www.tiktok.com/@glamsquad',
      'https://x.com/GlamSquad',
      'https://www.youtube.com/@_glamsquad',
      'https://www.linkedin.com/company/glamsquad/',
      // removing until these are improved
      // 'https://www.crunchbase.com/organization/glamsquad',
      // 'https://wellfound.com/company/glamsquad-2',
      // 'https://www.trustpilot.com/review/glamsquad.com',
    ],
    founder: [
      {
        '@type': 'Person',
        '@id': 'https://glamsquad.com/about-us#person-giovanni-vaccaro',
        name: 'Giovanni Vaccaro',
        jobTitle: 'Co-Founder',
        url: 'https://glamsquad.com/author/giovanni-vaccaro',
        image: 'https://glamsquad.com/about-us/gio.jpg',
        worksFor: { '@id': 'https://glamsquad.com/#organization' },
        sameAs: [
          'https://www.linkedin.com/in/giovanni-vaccaro-b4137a90/',
          'https://www.crunchbase.com/person/giovanni-vaccaro',
          'https://www.giovaccaro.com/',
          'https://www.instagram.com/giovannivaccaro/',
          'https://www.wherebrainsmeetbeauty.com/episode-240/',
          'https://thespotlyte.com/1123004/giovanni-vaccaro-interview',
        ],
      },
      {
        '@type': 'Person',
        '@id': 'https://glamsquad.com/about-us#person-david-goldweitz',
        name: 'David Goldweitz',
        jobTitle: 'Co-Founder & CEO',
        image: 'https://glamsquad.com/about-us/dave.jpg',
        worksFor: { '@id': 'https://glamsquad.com/#organization' },
        sameAs: [
          'https://www.linkedin.com/in/davidgoldweitz/',
          'https://www.crunchbase.com/person/david-goldweitz',
          'https://thelobbyingshow.libsyn.com/making-the-jump-from-wall-street-to-a-venture-backed-beauty-startup',
        ],
      },
    ],
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.9',
      ratingCount: '15002',
      bestRating: '5',
      worstRating: '1',
    },
    interactionStatistic: [
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/RegisterAction',
        userInteractionCount: 200000,
        name: 'Satisfied customers and happy members',
      },
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/ReserveAction',
        userInteractionCount: 1500000,
        name: 'Completed appointments',
      },
      {
        '@type': 'InteractionCounter',
        interactionType: 'https://schema.org/JoinAction',
        userInteractionCount: 2000,
        name: 'Nationwide beauty professionals',
      },
    ],
  },
}

// Breadcrumb trail (Home > About Us) for breadcrumb rich results in SERPs and to
// reinforce the site hierarchy. See
// https://developers.google.com/search/docs/appearance/structured-data/breadcrumb
const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  '@id': 'https://glamsquad.com/about-us#breadcrumb',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Home',
      item: 'https://glamsquad.com/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'About Us',
      item: 'https://glamsquad.com/about-us',
    },
  ],
}

const aboutPageSchemaScript = JSON.stringify(ABOUT_PAGE_SCHEMA)
const breadcrumbSchemaScript = JSON.stringify(BREADCRUMB_SCHEMA)

const AboutUs = () => {
  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <meta name="robots" content="index,follow,max-image-preview:large" />
        <link rel="canonical" href={CANONICAL} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="GLAMSQUAD" />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={CANONICAL} />
        <meta property="og:image" content={SHARE_IMAGE} />
        <meta property="og:image:width" content="1440" />
        <meta property="og:image:height" content="1496" />
        <meta property="og:image:alt" content="GLAMSQUAD founders Giovanni Vaccaro and David Goldweitz" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content={SHARE_IMAGE} />
        <meta name="twitter:image:alt" content="GLAMSQUAD founders Giovanni Vaccaro and David Goldweitz" />
        <script type="application/ld+json">{aboutPageSchemaScript}</script>
        <script type="application/ld+json">{breadcrumbSchemaScript}</script>
      </Head>
      <AboutUsWrapper>
        <Hero />
        <MissionStats />
        <Founders />
        <FoundersQuote />
        <TopTierTalent />
        <Partnerships />
        <TestimonialsCarousel />
        <PressLogos />
        <BookCTA />
        <MarketsMap />
        <FAQsCTA />
      </AboutUsWrapper>
    </>
  )
}

export default AboutUs
